import React, { useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Button,
  SimpleGrid,
  Image,
  Checkbox,
  ActionIcon,
  Badge,
  Menu,
  Loader,
  Center,
  Paper,
  SegmentedControl,
  ThemeIcon,
} from '@mantine/core';
import {
  IconPhoto,
  IconDownload,
  IconFilter,
  IconCalendar,
  IconCheck,
  IconX,
  IconZoomIn,
} from '@tabler/icons-react';
import { useIntersection } from '@mantine/hooks';
import { useGroupedPhotos, usePhotoDownload } from '../../../hooks/parent/useChildPhotos';
import type { ChildPhoto } from '../../../services/parentService';
import { renderLocation } from '../../../utils/locationUtils';

interface PhotoGalleryProps {
  childId: string;
  onPhotoClick?: (photo: ChildPhoto) => void;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({ childId, onPhotoClick }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  // const [filterDate, setFilterDate] = useState<string | undefined>();
  
  const {
    groupedPhotos,
    allPhotos,
    totalPhotos,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useGroupedPhotos(childId);

  const downloadMutation = usePhotoDownload();

  // Intersection observer for infinite scroll
  const { ref, entry } = useIntersection({
    root: null,
    threshold: 1,
  });

  React.useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const togglePhotoSelection = (photoId: string) => {
    const newSelection = new Set(selectedPhotos);
    if (newSelection.has(photoId)) {
      newSelection.delete(photoId);
    } else {
      newSelection.add(photoId);
    }
    setSelectedPhotos(newSelection);
  };

  const selectAll = () => {
    setSelectedPhotos(new Set(allPhotos.map(p => p.id)));
  };

  const clearSelection = () => {
    setSelectedPhotos(new Set());
    setSelectionMode(false);
  };

  const handleDownloadSelected = () => {
    if (selectedPhotos.size > 0) {
      downloadMutation.mutate(Array.from(selectedPhotos));
    }
  };

  const handlePhotoClick = (photo: ChildPhoto) => {
    if (selectionMode) {
      togglePhotoSelection(photo.id);
    } else if (onPhotoClick) {
      onPhotoClick(photo);
    }
  };

  if (isLoading) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading photos...</Text>
        </Stack>
      </Center>
    );
  }

  if (error) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size="xl" radius="xl" color="red" variant="light">
            <IconX size={32} />
          </ThemeIcon>
          <Text fw={500}>Error loading photos</Text>
          <Text size="sm" c="dimmed">Please try again later</Text>
        </Stack>
      </Card>
    );
  }

  if (allPhotos.length === 0) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size="xl" radius="xl" color="gray" variant="light">
            <IconPhoto size={32} />
          </ThemeIcon>
          <Text fw={500}>No photos yet</Text>
          <Text size="sm" c="dimmed">Photos will appear here as they are taken</Text>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="md">
      {/* Header Controls */}
      <Card withBorder p="md">
        <Group justify="space-between">
          <Group>
            <Text fw={600}>
              {totalPhotos} Photos
            </Text>
            {selectionMode && (
              <Badge color="blue" variant="light">
                {selectedPhotos.size} selected
              </Badge>
            )}
          </Group>

          <Group>
            {selectionMode ? (
              <>
                <Button
                  size="xs"
                  variant="light"
                  onClick={selectAll}
                  leftSection={<IconCheck size={14} />}
                >
                  Select All
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="red"
                  onClick={clearSelection}
                  leftSection={<IconX size={14} />}
                >
                  Cancel
                </Button>
                <Button
                  size="xs"
                  onClick={handleDownloadSelected}
                  disabled={selectedPhotos.size === 0}
                  loading={downloadMutation.isPending}
                  leftSection={<IconDownload size={14} />}
                >
                  Download ({selectedPhotos.size})
                </Button>
              </>
            ) : (
              <>
                <SegmentedControl
                  size="xs"
                  value={viewMode}
                  onChange={(value) => setViewMode(value as 'grid' | 'list')}
                  data={[
                    { label: 'Grid', value: 'grid' },
                    { label: 'List', value: 'list' },
                  ]}
                />
                <Menu shadow="md" width={200}>
                  <Menu.Target>
                    <ActionIcon variant="light" size="lg">
                      <IconFilter size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Filter by</Menu.Label>
                    <Menu.Item leftSection={<IconCalendar size={14} />}>
                      Today
                    </Menu.Item>
                    <Menu.Item leftSection={<IconCalendar size={14} />}>
                      This Week
                    </Menu.Item>
                    <Menu.Item leftSection={<IconCalendar size={14} />}>
                      This Month
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => setSelectionMode(true)}
                  leftSection={<IconCheck size={14} />}
                >
                  Select
                </Button>
              </>
            )}
          </Group>
        </Group>
      </Card>

      {/* Photos */}
      {viewMode === 'grid' ? (
        <Stack gap="lg">
          {Object.entries(groupedPhotos).map(([date, photos]) => (
            <div key={date}>
              <Group mb="sm">
                <IconCalendar size={16} />
                <Text size="sm" fw={600}>{date}</Text>
                <Badge size="sm" variant="light">{photos.length} photos</Badge>
              </Group>
              
              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="sm">
                {photos.map((photo) => (
                  <Paper
                    key={photo.id}
                    radius="md"
                    style={{ 
                      position: 'relative', 
                      cursor: 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={() => handlePhotoClick(photo)}
                  >
                    <Image
                      src={photo.thumbnail_url || photo.url}
                      alt={`Photo from ${photo.activity_type}`}
                      height={150}
                      fit="cover"
                    />
                    
                    {selectionMode && (
                      <Checkbox
                        checked={selectedPhotos.has(photo.id)}
                        onChange={() => togglePhotoSelection(photo.id)}
                        style={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    
                    <Badge
                      size="xs"
                      variant="filled"
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                      }}
                    >
                      {photo.activity_type}
                    </Badge>

                    {!selectionMode && (
                      <ActionIcon
                        size="sm"
                        variant="filled"
                        color="dark"
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          opacity: 0.8,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onPhotoClick?.(photo);
                        }}
                      >
                        <IconZoomIn size={14} />
                      </ActionIcon>
                    )}
                  </Paper>
                ))}
              </SimpleGrid>
            </div>
          ))}
        </Stack>
      ) : (
        <Stack gap="xs">
          {allPhotos.map((photo) => (
            <Card
              key={photo.id}
              withBorder
              p="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => handlePhotoClick(photo)}
            >
              <Group>
                {selectionMode && (
                  <Checkbox
                    checked={selectedPhotos.has(photo.id)}
                    onChange={() => togglePhotoSelection(photo.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
                <Image
                  src={photo.thumbnail_url || photo.url}
                  alt={`Photo from ${photo.activity_type}`}
                  width={60}
                  height={60}
                  radius="sm"
                  fit="cover"
                />
                <div style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {photo.activity_type} • {renderLocation(photo.location)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {new Date(photo.timestamp).toLocaleString()}
                  </Text>
                  {photo.teacher_notes && (
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {photo.teacher_notes}
                    </Text>
                  )}
                </div>
                {photo.tags && photo.tags.length > 0 && (
                  <Group gap="xs">
                    {photo.tags.slice(0, 2).map((tag: string, index: number) => (
                      <Badge key={index} size="xs" variant="light">
                        {tag}
                      </Badge>
                    ))}
                  </Group>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      {/* Load More Indicator */}
      <div ref={ref} style={{ height: 20 }}>
        {isFetchingNextPage && (
          <Center>
            <Loader size="sm" />
          </Center>
        )}
      </div>
    </Stack>
  );
};

export default PhotoGallery;